import type {
  FollowupDocumentView, FollowupEvidenceView, FollowupFieldChoiceActionEnum, FollowupFieldViewDecisionEnum,
  FollowupRecordChoiceActionEnum, FollowupRecordGroupViewMatchActionEnum, RecordAddress,
} from "@/lib/api/generated/casedb";
import type { CaseReviewSectionId } from "@/features/catalog_case_review/shared/constants/caseReviewSections";
import type { InternalDeadlineChipModel } from "@/features/catalog_case_review/shell/utils/internalDeadlineChip";
import type { FollowupReviewCounts } from "@/features/catalog_case_review/shared/model/caseReview";
import type { CaseFieldInputKind } from "@/features/catalog_case_review/shared/components/field_review/CaseFieldInput";
import type { CaseFieldOption, CaseFieldValue } from "@/features/catalog_case_review/shared/model/caseReview";

/** The three choices a reviewer can make on one proposal. */
export type FollowupFieldAction = Extract<FollowupFieldChoiceActionEnum, "ACCEPTED" | "KEPT" | "EDITED">;
/** The three choices on an incoming record. */
export type FollowupRecordAction = Extract<FollowupRecordChoiceActionEnum, "MATCH" | "CREATE" | "EXCLUDE">;

/** Where a follow-up item lives in the case, in the terms the section renderers use. */
export interface FollowupFieldLocation {
  sectionId: CaseReviewSectionId;
  /** Full catalog collection id (`case.product.products.dosage_regimens`); undefined for a scalar section field. */
  collectionId?: string;
  /** The row id as the review document spells it; undefined for a scalar field. */
  recordId?: string;
  /** The control key inside its form: `city`, or `reported_causality.source`. */
  key: string;
  fieldId: string;
}

export interface FollowupFieldDraft { action: FollowupFieldAction; editedValue?: CaseFieldValue }
export interface FollowupRecordDraft {
  action: FollowupRecordAction;
  target?: RecordAddress;
  /** Create only: values the reviewer typed for required fields the follow-up does not carry, by catalog field id. */
  additionalValues?: Readonly<Record<string, CaseFieldValue>>;
}

/** A required field of a record being created that the follow-up does not carry, offered as an ordinary input. */
export interface FollowupAddedFieldView {
  fieldId: string;
  location: FollowupFieldLocation;
  label: string;
  kind: CaseFieldInputKind;
  options?: CaseFieldOption[];
  value: CaseFieldValue;
  /** The server's refusal of the last save for this field, until the next save attempt. */
  error?: string;
}

export interface FollowupProposalView {
  itemId: number;
  rowVersion: number;
  followupId: number;
  arrivalOrdinal: number;
  location: FollowupFieldLocation;
  /** The record is a target of the case (matched, created, or scalar). Unresolved incoming records have no inline row. */
  resolved: boolean;
  label: string;
  kind: CaseFieldInputKind;
  options?: CaseFieldOption[];
  currentValue: CaseFieldValue;
  currentPresent: boolean;
  incomingValue: CaseFieldValue;
  decision: FollowupFieldViewDecisionEnum;
  /** Pending and its record resolved (or a CREATE draft exists): the reviewer may decide it now. */
  decidable: boolean;
  draft?: FollowupFieldDraft;
  incomingEvidence?: FollowupEvidenceView;
  currentEvidence?: FollowupEvidenceView;
  decidedByName?: string;
  decidedAt?: string;
  /** The server's refusal of the last save for this item, until the next save attempt. */
  error?: string;
}

export interface FollowupCandidateView { address: RecordAddress; title: string }

/** What an incoming repeated record may be answered with, in the record type's own words. */
export interface FollowupRecordChoices {
  /** The record type in a sentence: "event", "event assessment". */
  noun: string;
  /** A reviewer may add one: not for rows the system creates (event assessments, datasheets), nor under an ignored parent. */
  canCreate: boolean;
  /** Set while the record this one depends on (its parent, or an assessment's event) has no answer: none offered yet. */
  waitsFor?: { noun: string };
  /** An assessment whose product and event are answered "Same as": the save matches it with the parent's. */
  matchedOnSave?: boolean;
}

export interface FollowupGroupView {
  /** `${followupId}|${encoded source address}` for a record group; `${followupId}|${sectionId}` for that arrival's scalar fields of one section. */
  key: string;
  followupId: number;
  arrivalOrdinal: number;
  sectionId: CaseReviewSectionId;
  /** Rail group key when the group belongs to one subsection, for the count badge. */
  groupKey?: string;
  breadcrumb: string[];
  /** An incoming repeated record that still needs Match / Create / Exclude. */
  incoming: boolean;
  matchAction: FollowupRecordGroupViewMatchActionEnum;
  candidates: FollowupCandidateView[];
  /** False while the parent record has neither a saved match nor a MATCH draft: there is no record of it to be the same as. */
  canMatch: boolean;
  /** The answers an incoming repeated record offers; undefined for a section's own fields. */
  choices?: FollowupRecordChoices;
  /** Saved EXCLUDED: shown struck through with no action. A drafted EXCLUDE offers Undo. */
  excludedSaved: boolean;
  sourceAddress?: RecordAddress;
  targetAddress?: RecordAddress;
  draft?: FollowupRecordDraft;
  items: FollowupProposalView[];
  /** A CREATE draft's required fields the follow-up lacks; empty for every other group. */
  addedFields: FollowupAddedFieldView[];
  /** The location used by Open in case: the group's first item. */
  openTarget?: FollowupFieldLocation;
}

export interface FollowupArrivalSummary {
  id: number;
  ordinal: number;
  label: string;
  documentName: string;
  receivedDate: string;
  pendingCount: number;
  status: string;
  /** What the reviewer recorded when attaching it (issue #52): false / empty when not recorded. */
  significant: boolean;
  amendment: boolean;
  justification: string;
  /** "Answers query Q-n" when the arrival answers a query (#264, mock screen 6); undefined otherwise. */
  answersQuery: string | undefined;
  /** The arrival revision that a receipt date correction sends back. */
  rowVersion: number;
  /** The arrival's own internal due date; undefined when it has no result yet. */
  deadline?: InternalDeadlineChipModel;
}

export interface FollowupDocumentOption { document: FollowupDocumentView; label: string; active: boolean }

/** What has happened to an incoming repeated record, for its chip subtitle and its detail block. */
export type FollowupRecordState = "incoming" | "matched" | "created" | "excluded";

/** One incoming record of a collection: its group, what it is called, where it stands, and the collections it owns. */
export interface FollowupRecordBlock {
  group: FollowupGroupView;
  title: string;
  state: FollowupRecordState;
  /** The state comes from an unsaved draft rather than the server. */
  unsaved: boolean;
  pending: number;
  children: FollowupCollectionBlock[];
}

/** One collection of a section (or of a parent record) that this arrival touched. */
export interface FollowupCollectionBlock {
  /** `${owner group key or section id}|${collectionId}`; unique within one arrival's outline. */
  key: string;
  collectionId: string;
  label: string;
  records: FollowupRecordBlock[];
}

/** One section of the case as this arrival touched it: its scalar proposals, then its collections. */
export interface FollowupSectionBlock {
  sectionId: CaseReviewSectionId;
  label: string;
  openTarget?: FollowupFieldLocation;
  scalars: FollowupProposalView[];
  collections: FollowupCollectionBlock[];
  pending: number;
}

export interface FollowupReviewSummary {
  arrivals: FollowupArrivalSummary[];
  groups: FollowupGroupView[];
  /** Arrival id → that arrival's proposals laid out as the case is: section, collection, record. */
  outlines: Map<number, FollowupSectionBlock[]>;
  /** `followupBadgeKey(fieldId, recordId)` → proposals on that field, for the inline badge. Only resolved proposals are indexed. */
  badgeIndex: Map<string, FollowupProposalView[]>;
  /** Field ids (with record) that carry a PENDING proposal, so the ordinary input is locked with FOLLOWUP_FIELD_LOCKED_BY_PROPOSAL. */
  lockedFields: Set<string>;
  /** PENDING incoming proposals, counted once, everywhere (header, rail, list). Not the server's pending_count, which also counts lineage rows. */
  pendingTotal: number;
  itemTotal: number;
  pendingBySection: Partial<Record<CaseReviewSectionId, number>>;
  pendingByGroup: Record<string, number>;
  /** Follow-up work per section and per rail group (`section|group`): decidable proposals and incoming records. */
  scopeBySection: Partial<Record<CaseReviewSectionId, FollowupReviewCounts>>;
  scopeByGroup: Record<string, FollowupReviewCounts>;
  documents: FollowupDocumentOption[];
}

/**
 * `useFollowupReviewModel`'s result: `undefined` when the case has no follow-up arrivals at all, a
 * summary when the view built, or a caught build error — never a default or a swallowed exception.
 * The workspace shows the error and still renders the rest of Case Review; a bad follow-up payload
 * must not take down a reviewer's whole case.
 */
export type FollowupReviewResult = { summary: FollowupReviewSummary } | { error: Error };

export interface FollowupSourceSelection {
  document: FollowupDocumentView;
  evidence: FollowupEvidenceView;
  fieldId: string;
  value: unknown;
}

/** Full catalog field id plus the row, because `is_primary` on product 8 and on event 8 are different fields. */
export const followupBadgeKey = (fieldId: string, recordId: string | undefined): string => `${fieldId}@${recordId ?? ""}`;
